import React, { useEffect, useState } from "react";
import { Button, Rate, Popconfirm } from "antd";
import {
  EditOutlined,
  DeleteOutlined
} from "@ant-design/icons";

const cloneRatings = (ratings = {}) => ({
  general_skills: (ratings.general_skills || []).map(
    (skill) => ({ ...skill })
  ),

  event_roles: (ratings.event_roles || []).map(
    (skill) => ({ ...skill })
  ),

  skills: (ratings.skills || []).map(
    (skill) => ({ ...skill })
  )
});

const SkillCard = ({
  label,
  value,
  isEditing,
  onChange
}) => (
  <div className="border border-grayBorder rounded-xl p-4 bg-white shadow-sm hover:shadow transition h-fit">
    <div className="flex justify-between items-start">

      <div className="flex items-center">
        <div className="font-medium text-gray-800">
          {label}
        </div>
      </div>

      <div className="text-right mt-[3px] flex items-center">

        <div className="text-sm font-semibold">
          {Number(value || 0).toFixed(1)}
        </div>

        <Rate
          disabled={!isEditing}
          allowHalf
          value={Number(value || 0)}
          onChange={onChange}
          style={{
            fontSize: 15,
            marginLeft: 8,
            marginTop: 3
          }}
        />

      </div>

    </div>
  </div>
);

const RoleSkillRow = ({
  label,
  value,
  isEditing,
  onChange
}) => (
  <div className="flex items-center justify-between px-6 py-5 border-b border-grayBorder last:border-b-0">

    <div className="font-medium text-gray-800">
      {label}
    </div>

    <div className="flex items-center gap-4">

      <span className="font-semibold text-md">
        {Number(value || 0).toFixed(1)}
      </span>

      <Rate
        disabled={!isEditing}
        allowHalf
        value={Number(value || 0)}
        onChange={onChange}
        style={{
          fontSize: 15,
          marginLeft: 8,
          marginTop: 3
        }}
      />

    </div>

  </div>
);

const ExpandedFeedback = ({
  feedback,
  canEdit = false,
  canDelete = false,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  onDelete
}) => {
  const {
    ratings = {},
    desc
  } = feedback;

  /*
   * initialRatings = source of truth for the current feedback
   * editedRatings = values currently being edited
   */
  const [initialRatings, setInitialRatings] = useState(
    () => cloneRatings(ratings)
  );

  const [editedRatings, setEditedRatings] = useState(
    () => cloneRatings(ratings)
  );

  /*
   * desc IS the additional feedback text now — no marker/section
   * parsing needed, ratings live entirely in stats_obj.ratings.*
   */
  const initialAdditionalFeedback = desc || "";

  const [
    initialAdditionalFeedbackState,
    setInitialAdditionalFeedbackState
  ] = useState(initialAdditionalFeedback);

  const [
    editedAdditionalFeedback,
    setEditedAdditionalFeedback
  ] = useState(initialAdditionalFeedback);

  /*
   * When feedback from Redux changes after a successful save,
   * update both initial and editable values.
   */
  useEffect(() => {
    const newRatings = cloneRatings(ratings);
    const newAdditionalFeedback = desc || "";

    setInitialRatings(newRatings);
    setEditedRatings(newRatings);

    setInitialAdditionalFeedbackState(
      newAdditionalFeedback
    );

    setEditedAdditionalFeedback(
      newAdditionalFeedback
    );
  }, [ratings, desc]);

  /*
   * When Edit is clicked, make a fresh editable copy
   * from the initial values.
   */
  useEffect(() => {
    if (isEditing) {
      setEditedRatings(
        cloneRatings(initialRatings)
      );

      setEditedAdditionalFeedback(
        initialAdditionalFeedbackState
      );
    }
  }, [
    isEditing,
    initialRatings,
    initialAdditionalFeedbackState
  ]);

  const generalSkills = (
    editedRatings.general_skills || []
  ).filter(
    skill =>
      skill.label &&
      skill.label !== "—"
  );

  const roleSkills = [
    ...(editedRatings.event_roles || []),
    ...(editedRatings.skills || [])
  ].filter(
    skill =>
      skill.label &&
      skill.label !== "—"
  );

  const updateRating = (
    section,
    uuid,
    value
  ) => {
    setEditedRatings(prev => ({
      ...prev,

      [section]: (prev[section] || []).map(item =>
        item.uuid === uuid
          ? {
              ...item,
              rating: value
            }
          : item
      )
    }));
  };

  const handleSave = () => {
    onSave?.({
      ratings: cloneRatings(editedRatings),
      additionalFeedback:
        editedAdditionalFeedback
    });
  };

  const handleCancel = () => {
    /*
     * Throw away all changes and restore
     * the values that existed when editing started.
     */
    setEditedRatings(
      cloneRatings(initialRatings)
    );

    setEditedAdditionalFeedback(
      initialAdditionalFeedbackState
    );

    onCancel?.();
  };

  return (
    <div className="bg-white border-t border-grayBorder px-4 py-6">

      {/* GENERAL SKILLS */}
      {!!generalSkills.length && (
        <div>

          <h3 className="text-sm uppercase tracking-wide font-semibold text-gray-500 mb-5">
            General Skills
          </h3>

          <div className="grid grid-cols-2 gap-x-5 gap-y-2">

            {generalSkills.map(skill => (
              <SkillCard
                key={skill.uuid}
                label={skill.label}
                value={skill.rating}
                isEditing={isEditing}
                onChange={(value) =>
                  updateRating(
                    "general_skills",
                    skill.uuid,
                    value
                  )
                }
              />
            ))}

          </div>

        </div>
      )}

      {/* ROLE SPECIFIC */}
      {!!roleSkills.length && (
        <div className="mt-[25px]">

          <h3 className="text-sm uppercase tracking-wide font-semibold text-gray-500 mb-5">
            Role-Specific Skills
          </h3>

          <div className="border border-grayBorder rounded-xl overflow-hidden bg-white shadow-sm">

            {editedRatings.event_roles?.map(skill => (
              skill.label &&
              skill.label !== "—" && (
                <RoleSkillRow
                  key={skill.uuid}
                  label={skill.label}
                  value={skill.rating}
                  isEditing={isEditing}
                  onChange={(value) =>
                    updateRating(
                      "event_roles",
                      skill.uuid,
                      value
                    )
                  }
                />
              )
            ))}

            {editedRatings.skills?.map(skill => (
              skill.label &&
              skill.label !== "—" && (
                <RoleSkillRow
                  key={skill.uuid}
                  label={skill.label}
                  value={skill.rating}
                  isEditing={isEditing}
                  onChange={(value) =>
                    updateRating(
                      "skills",
                      skill.uuid,
                      value
                    )
                  }
                />
              )
            ))}

          </div>

        </div>
      )}

      {/* ADDITIONAL FEEDBACK */}
      <div className="mt-[25px]">

        <h3 className="text-sm uppercase tracking-wide font-semibold text-gray-500 mb-5">
          Additional Feedback
        </h3>

        {isEditing ? (
          <div className="relative">

            <textarea
              value={editedAdditionalFeedback}
              onChange={(e) =>
                setEditedAdditionalFeedback(
                  e.target.value
                )
              }
              maxLength={2048}
              rows={4}
              className="w-full rounded-lg border border-grayBorder p-3 pb-8 outline-none focus:border-blue-500"
              placeholder="Add any additional feedback not captured in the ratings above."
            />

            <span className="absolute bottom-3 right-3 text-xs text-gray-400">
              {editedAdditionalFeedback.length}/2048
            </span>

          </div>
        ) : (
          !!editedAdditionalFeedback && (
            <div className="bg-catskill rounded-xl p-4 text-gray-700 leading-8 whitespace-pre-wrap">
              {editedAdditionalFeedback}
            </div>
          )
        )}

      </div>

      {/* ACTIONS */}
      <div className="flex justify-between items-center mt-8">

        <div>
          {isEditing && (
            <Button
              danger
              icon={<DeleteOutlined />}
              size="large"
              onClick={onDelete}
              disabled={!canDelete}
              className="disabled:!bg-gray-300 disabled:!text-gray-500 disabled:!border-gray-300 disabled:!cursor-not-allowed"
            >
              Delete
            </Button>
          )}
        </div>

        <div className="flex gap-2">

          {isEditing ? (
            <>
              <Button
                size="large"
                onClick={handleCancel}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                size="large"
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              icon={<EditOutlined />}
              onClick={onEdit}
              size="large"
              disabled={!canEdit}
              className="disabled:!bg-gray-300 disabled:!text-gray-500 disabled:!border-gray-300 disabled:!cursor-not-allowed"
            >
              Edit Review
            </Button>
          )}

        </div>

      </div>

    </div>
  );
};

export default ExpandedFeedback;